import { BaseRateRowRecord, Dimension } from "models/ratecard"
import { css, cx } from "@emotion/css"

import { BREAKDOWN_SEMANTIC_CODE } from "routes/Base/Admin/MarketVendors/Details/RateCard/RateCardDetails/BaseRate/utils"
import { EmptyState } from "components/EmptyState"
import { computeDerivedDurationValues } from "routes/Base/Admin/MarketVendors/Details/RateCard/RateCardDetails/BaseRate/RatesTable"
import { formatFloatingPointNumber } from "utils/number-format"
import { hourLabelFromTime } from "components/HourPicker"
import { useI18n } from "providers/I18n"
import { useAvailableTargets } from "../utils"

type BaseRateTableProps = {
  handleBaseRatePick: (
    baseRateId: string,
    breakdown: React.Key,
    unitCost: number,
  ) => void
  pickedValues?: {
    baseRateId: string
    breakdown: React.Key
    unitCost: number
  }
  dimensions?: Dimension[]
  data?: BaseRateRowRecord[]
}

export function RateCardTable(props: BaseRateTableProps) {
  const { t } = useI18n()
  const { dimensions, data, pickedValues, handleBaseRatePick } = props
  const noData = !data?.length
  const availableTargets = useAvailableTargets()

  const getTargetName = (id?: string) =>
    availableTargets?.find((t) => t?.id === id)?.name

  if (noData) return <EmptyState title={t("no.base.rate.found")} />

  const flat = dimensions?.filter(
    (d) => d.mediaFieldSemanticCode !== BREAKDOWN_SEMANTIC_CODE && d.enabled,
  )
  const nested = dimensions?.find(
    (d) => d.mediaFieldSemanticCode === BREAKDOWN_SEMANTIC_CODE,
  )

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {!!flat?.length ? (
            flat?.map((d) => (
              <th
                className="BodyBody-sDark-greyRegular"
                key={d.mediaFieldSemanticCode}
              >
                {t(d.mediaFieldSemanticCode)}
              </th>
            ))
          ) : (
            <th className="empty"></th>
          )}
          <th
            className={cx("BodyBody-sDark-greyRegular", "nested")}
            colSpan={nested?.options?.length}
          >
            {t(nested?.mediaFieldSemanticCode || "")}
          </th>
        </tr>
        <tr>
          <th colSpan={flat?.length} className="empty"></th>
          {nested?.options.map((n, idx) => (
            <th
              className={cx("BodyBody-2BlackBold", "nested")}
              key={`base.rates.nested.header.${idx}`}
            >
              {n.value}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data?.map((r, idx) => {
          const manualDefaultKey = nested?.options.find(
            (o) => o.type === "MANUAL" && o.isDefault,
          )?.value
          const manualDefaultValue =
            manualDefaultKey && nested
              ? r[nested.mediaFieldSemanticCode][manualDefaultKey]
              : 0

          return (
            <tr key={`base.rates.${idx}`}>
              {!!flat?.length ? (
                flat?.map((f) => {
                  let fvalue = r[f.mediaFieldSemanticCode]
                  switch (f?.mediaFieldSemanticCode) {
                    case "daysOfWeek":
                      fvalue = r[f.mediaFieldSemanticCode]
                        ?.map((dow: string) => t(dow))
                        .join(", ")
                      break
                    case "timebelt":
                      fvalue = [
                        hourLabelFromTime(r[f.mediaFieldSemanticCode]?.start),
                        hourLabelFromTime(r[f.mediaFieldSemanticCode]?.end),
                      ].join(" - ")
                      break
                    case "targetB":
                      fvalue = getTargetName(r[f.mediaFieldSemanticCode]?.[0])
                      break
                    default:
                      break
                  }

                  return (
                    <td
                      className="BodyBody-2BlackRegular"
                      key={`base.rates.${idx}.${f.mediaFieldSemanticCode}`}
                    >
                      {fvalue}
                    </td>
                  )
                })
              ) : (
                <td className="empty"></td>
              )}
              {nested?.options.map((o, idx) => {
                const isManual = o.type === "MANUAL"

                const value = isManual
                  ? r[nested.mediaFieldSemanticCode][o.value]
                  : computeDerivedDurationValues(
                      manualDefaultValue,
                      o.percentage ?? 100,
                    )

                const isActive =
                  r.id === pickedValues?.baseRateId &&
                  o.value === pickedValues.breakdown

                return (
                  <td
                    onClick={(_) => handleBaseRatePick(r.id, o.value, value)}
                    key={`bas.rates.breakdown.${idx}`}
                    className={cx("BodyBody-2BlackRegular", "clickable", {
                      [styles.active]: isActive,
                    })}
                  >
                    {formatFloatingPointNumber(2)(value)}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

const styles = {
  table: css`
    width: 100%;
    border-collapse: collapse;
    tr {
      border: 1px solid var(--light-grey);
      border-left-width: 0;
      border-right-width: 0;
      height: 40px;
      &:last-of-type {
        border-bottom: none;
      }
      .clickable {
        cursor: pointer;
        border-radius: 2px;
        text-align: center;
        &:hover {
          box-shadow:
            inset 2px 2px var(--primary-main),
            inset -2px -2px var(--primary-main);
        }
      }
      .nested {
        text-align: center;
      }
      .empty {
        padding: 0;
        width: 0;
      }
    }
    ,
    th,
    td {
      padding-left: var(--spacing-xxs);
      padding-right: var(--spacing-xxs);
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
      text-align: left;
    }
    thead {
      tr {
        border-top: none;
      }
    }
  `,
  active: css`
    box-shadow:
      inset 2px 2px var(--primary-main),
      inset -2px -2px var(--primary-main);
  `,
}
